import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials } from '@/mocks/mahjongData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && typeof value === 'string') {
          payload.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9s8jlfnahb7382ovs60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20welcoming%20traditional%20Chinese%20office%20space%20with%20mahjong%20elements%2C%20elegant%20wooden%20furniture%2C%20soft%20natural%20lighting%2C%20modern%20yet%20traditional%20aesthetic%2C%20professional%20atmosphere%2C%20clean%20and%20bright%2C%20corporate%20photography&width=1800&height=600&seq=about-hero&orientation=landscape"
            alt="关于麻将电脑版在线玩"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl text-white mb-3">关于我们</h1>
              <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto">
                {siteInfo.slogan}
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1024px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">品牌介绍</h2>
              <p className="text-foreground-600 leading-relaxed max-w-3xl mx-auto">
                {siteInfo.name}成立于{siteInfo.founded}，是国内领先的网页麻将在线游戏平台。
                我们致力于为全球麻将爱好者提供最便捷、最优质的在线麻将游戏体验。
                从经典麻将到地方特色玩法，从单机练习到多人联机，我们不断丰富产品功能，
                只为让每一位玩家都能在这里找到属于自己的麻将乐趣。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto bg-primary-100 text-primary-600 rounded-lg mb-4">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每一位麻将爱好者都能随时随地享受麻将的乐趣，打破时间和空间的限制，连接全球麻友。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-flag-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打造最专业、最全面的免费在线麻将平台，传承和推广中国传统麻将文化，让国粹焕发新的活力。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto bg-secondary-100 text-secondary-700 rounded-lg mb-4">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  用户至上、产品为本。坚持免费开放，持续优化体验，用心服务每一位麻将爱好者。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-primary-500">
          <div className="w-full px-4 md:px-6 max-w-[1024px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-3xl md:text-4xl font-heading text-background-50">{siteInfo.users}</p>
                <p className="text-sm text-primary-100 mt-2">注册玩家</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading text-background-50">{siteInfo.games}</p>
                <p className="text-sm text-primary-100 mt-2">累计开局</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading text-background-50">{siteInfo.categories}+</p>
                <p className="text-sm text-primary-100 mt-2">玩法种类</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-heading text-background-50">4.9</p>
                <p className="text-sm text-primary-100 mt-2">用户评分</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">玩家口碑</h2>
              <p className="text-foreground-600">来自全国各地麻将爱好者的真实评价</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200 rounded-lg p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.comment}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 md:py-20 bg-secondary-50" id="contact">
          <div className="w-full px-4 md:px-6 max-w-[800px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">联系我们</h2>
              <p className="text-foreground-600">
                有任何问题或建议？欢迎随时与我们联系，我们会在24小时内回复
              </p>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              {formStatus === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto bg-primary-100 text-primary-600 rounded-full mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-foreground-600">感谢您的留言，我们会尽快与您联系。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-2 border border-primary-300 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'error' && formError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto bg-primary-100 text-primary-600 rounded-lg mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto bg-accent-100 text-accent-600 rounded-lg mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">客服电话</p>
                <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto bg-secondary-100 text-secondary-700 rounded-lg mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">官方微信</p>
                <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${siteInfo.name}`,
            description: `${siteInfo.name}的品牌介绍、口碑评价与联系方式`,
            url: 'https://mahjong-online.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: siteInfo.name,
              url: 'https://mahjong-online.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://mahjong-online.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}